import type {
  PostAdminV1StoresStoreIdOrdersIdDesignBody,
  PostAdminV1StoresStoreIdOrdersIdMarkPaidBody,
  PostalAddress,
  PostalAddressInput,
} from '@nucleus-commerce/api-client/admin';
import { fromZonedInput, type Money } from '@nucleus-commerce/core';

import type { AddressFormInput, AddressFormOutput } from '../schemas/address-schema';
import type { SendDesignFormOutput } from '../schemas/design-schema';
import type { MarkPaidFormInput, MarkPaidFormOutput } from '../schemas/mark-paid-schema';

/** `today` is the store's calendar day (`format.dateKey(new Date())`). */
export function emptyMarkPaidForm(outstanding: Money, today: string): MarkPaidFormInput {
  return { partial: false, amount: outstanding, reference: '', paidAt: today, note: '' };
}

/**
 * A full settlement omits `amount` so the backend takes the balance it holds, not ours. The
 * receipt date is midnight of that day in the store's time zone.
 */
export function toMarkPaidBody(
  values: MarkPaidFormOutput,
  timeZone: string,
): PostAdminV1StoresStoreIdOrdersIdMarkPaidBody {
  const paidAt = values.paidAt ? fromZonedInput(`${values.paidAt}T00:00`, timeZone) : null;
  return {
    ...(values.partial && values.amount ? { amount: values.amount } : {}),
    ...(values.reference ? { reference: values.reference } : {}),
    ...(paidAt ? { paidAt } : {}),
    ...(values.note ? { note: values.note } : {}),
  };
}

export function fromAddress(address: PostalAddress | null): AddressFormInput {
  return {
    firstName: address?.firstName ?? '',
    lastName: address?.lastName ?? '',
    company: address?.company ?? '',
    line1: address?.line1 ?? '',
    line2: address?.line2 ?? '',
    city: address?.city ?? '',
    region: address?.region ?? '',
    postalCode: address?.postalCode ?? '',
    countryCode: address?.countryCode ?? '',
    phone: address?.phone ?? '',
  };
}

const orNull = (value: string) => (value === '' ? null : value);

export function toAddressInput(values: AddressFormOutput): PostalAddressInput {
  return {
    firstName: orNull(values.firstName),
    lastName: values.lastName,
    company: orNull(values.company),
    line1: values.line1,
    line2: orNull(values.line2),
    city: values.city,
    region: orNull(values.region),
    postalCode: orNull(values.postalCode),
    countryCode: values.countryCode,
    phone: orNull(values.phone),
  };
}

/** No approvalUrl: the backend builds the link from the store's primary domain. */
export function toSendDesignBody(
  values: SendDesignFormOutput,
): PostAdminV1StoresStoreIdOrdersIdDesignBody {
  return { mediaId: values.mediaId, ...(values.message ? { message: values.message } : {}) };
}
